import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsApiReady } from "../hooks/use-api-ready";
import {
  groupScriptControllerGetSingle,
  groupScriptControllerPatchScript,
  type PatchScriptBodyDto,
  type ScriptDto,
} from "../lib/gen-api";

export const useScriptData = (
  groupUuid: string | null,
  scriptUuid: string | null,
) => {
  const queryClient = useQueryClient();

  const {
    data: script,
    isLoading,
    error,
  } = useQuery({
    enabled: useIsApiReady() && !!groupUuid && !!scriptUuid,
    initialData: null,
    queryKey: ["script", groupUuid, scriptUuid],
    queryFn: () =>
      groupScriptControllerGetSingle({
        path: {
          scriptUuid: scriptUuid!,
        },
      }).then((res) => res.data ?? null),
  });

  const { mutateAsync: updateScript } = useMutation({
    mutationFn: async (vars: PatchScriptBodyDto) => {
      if (!script) {
        throw new Error("Script not found");
      }

      return groupScriptControllerPatchScript({
        path: {
          scriptUuid: scriptUuid!,
        },
        body: {
          name: vars.name ?? script.name,
          content: vars.content ?? script.content,
        },
      });
    },
    onMutate: async (vars) => {
      if (!script || !groupUuid || !scriptUuid) {
        return;
      }

      await queryClient.cancelQueries({
        queryKey: ["script", groupUuid, scriptUuid],
      });

      const previousScript = queryClient.getQueryData([
        "script",
        groupUuid,
        scriptUuid,
      ]);

      queryClient.setQueryData(
        ["script", groupUuid, scriptUuid],
        (old: ScriptDto | null) => {
          if (!old) return null;
          return {
            ...old,
            name: vars.name ?? old.name,
            content: vars.content ?? old.content,
          };
        },
      );

      return { previousScript };
    },
    onError: (_err, _vars, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousScript && groupUuid && scriptUuid) {
        queryClient.setQueryData(
          ["script", groupUuid, scriptUuid],
          context.previousScript,
        );
      }
    },
    onSettled: () => {
      if (groupUuid && scriptUuid) {
        queryClient.invalidateQueries({
          queryKey: ["script", groupUuid, scriptUuid],
        });
        queryClient.invalidateQueries({
          queryKey: ["scripts", groupUuid],
        });
      }
    },
  });

  return { script, isLoading, error, updateScript };
};
